<template>
  <div class="login-page">
    <div class="bg"></div>
    <div class="login-card">
      <div class="brand">
        <div class="logo">BYMY</div>
        <div class="title">后台登录</div>
        <div class="sub">请使用账号密码登录</div>
      </div>
      <div class="login-form">
        <el-form :model="form" :rules="rules" ref="formRef" label-position="top" autocomplete="on" @submit.prevent="onSubmit">
          <el-form-item label="账号" prop="username" required>
            <el-input v-model="form.username" name="username" autocomplete="username" placeholder="请输入账号" clearable></el-input>
          </el-form-item>
          <el-form-item label="密码" prop="password" required>
            <el-input v-model="form.password" name="password" autocomplete="current-password" type="password" placeholder="请输入密码" show-password></el-input>
          </el-form-item>
          <el-form-item label="验证码" prop="captcha" required>
            <div class="captcha-row">
              <el-input v-model="form.captcha" placeholder="请输入验证码" maxlength="5"></el-input>
              <div class="captcha-box" @click="fetchCaptcha">
                <img v-if="captchaImg" :src="captchaImg" alt="captcha" class="captcha-img" />
                <span v-else-if="captchaText" class="captcha-text">{{ captchaText }}</span>
                <span v-else class="captcha-placeholder">点击刷新</span>
              </div>
            </div>
          </el-form-item>
          <div class="remember-row">
            <el-checkbox v-model="form.remember">记住账号</el-checkbox>
          </div>
          <el-button type="primary" :loading="loading" class="btn-login" native-type="submit">登 录</el-button>
        </el-form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted } from 'vue'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { useRouter } from 'vue-router'
import { getDefaultLandingPath, setAuthSession } from '@/utils/auth'
import { loadSiteConfig } from '@/utils/siteConfig'

const router = useRouter()
const formRef = ref(null)
const loading = ref(false)

const form = reactive({
  username: '',
  password: '',
  captcha: '',
  captchaId: '',
  remember: true
})
const captchaImg = ref('')
const captchaText = ref('')

const rules = {
  username: [{ required: true, message: '请输入账号', trigger: 'blur' }],
  password: [{ required: true, message: '请输入密码', trigger: 'blur' }],
  captcha: [{ required: true, message: '请输入验证码', trigger: 'blur' }]
}

const fetchCaptcha = async () => {
  try {
    const res = await axios.get('/api/captcha/image')
    const data = res.data?.data || {}
    form.captchaId = data.captchaId
    captchaImg.value = data.img || ''
    captchaText.value = data.code || ''
    form.captcha = ''
  } catch (e) {
    ElMessage.error('获取验证码失败')
  }
}

const handleRemember = () => {
  if (form.remember) {
    localStorage.setItem('adminLogin', JSON.stringify({ username: form.username, password: form.password }))
  } else {
    localStorage.removeItem('adminLogin')
  }
}

const loadRemember = () => {
  const saved = localStorage.getItem('adminLogin')
  if (saved) {
    try {
      const data = JSON.parse(saved)
      form.username = data.username || ''
      form.password = data.password || ''
      form.remember = true
    } catch (e) {
      localStorage.removeItem('adminLogin')
    }
  }
}

const onSubmit = () => {
  formRef.value?.validate(async valid => {
    if (!valid) return
    loading.value = true
    try {
      // 根据后端接口调整 url / 参数
      const res = await axios.post('/api/login', {
        username: form.username,
        password: form.password,
        captcha: form.captcha,
        captchaId: form.captchaId
      })
      const data = res.data?.data || res.data || {}
      const token = data.token
      if (token) {
        if (token) {
          handleRemember()
          setAuthSession({
            token,
            userId: data.userId,
            userName: data.userName,
            userNick: data.nickName,
            roleCodes: data.roleCodes || [],
          })
          ElMessage.success('登录成功')
          router.push(getDefaultLandingPath())
        }
      } else {
        ElMessage.error(res.data?.msg || '登录失败')
        fetchCaptcha()
      }
    } catch (e) {
      ElMessage.error(e?.response?.data?.message || '登录失败')
      fetchCaptcha()
    } finally {
      loading.value = false
    }
  })
}

onMounted(() => {
  loadRemember()
  fetchCaptcha()
  loadSiteConfig()
})
</script>

<style scoped>
.login-page {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at 20% 20%, rgba(255,255,255,0.12), transparent 35%),
              radial-gradient(circle at 80% 0%, rgba(255,255,255,0.12), transparent 30%),
              linear-gradient(145deg, #182848 0%, #2980b9 60%, #34b3ff 100%);
  overflow: hidden;
}
.bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255,255,255,0.05), transparent 40%);
}
.login-card {
  position: relative;
  width: 420px;
  padding: 36px 32px 30px;
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 28px 60px rgba(0,0,0,0.15);
  z-index: 1;
}
.brand {
  text-align: center;
  margin-bottom: 20px;
}
.logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(135deg, #2a81ff, #4bc3ff);
  color: #fff;
  font-weight: 800;
  letter-spacing: 1px;
  margin-bottom: 10px;
  font-size: 18px;
}
.title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}
.sub {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}
.actions {
  margin-top: 12px;
  display: flex;
  justify-content: center;
}
.btn-login {
  width: 100%;
  height: 44px;
  font-size: 15px;
  letter-spacing: 1px;
  border-radius: 24px;
}
.captcha-row {
  display: grid;
  grid-template-columns: 1fr 120px;
  gap: 10px;
  align-items: center;
}
.captcha-box {
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #2a81ff, #4bc3ff);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 2px;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
}
.captcha-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}
.captcha-placeholder {
  color: #fff;
  font-size: 12px;
}
</style>
